import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { XIcon } from '@heroicons/react/solid';
import Upload from '../upload';
import { compressImages } from '../../utils/compress-image';

let uploadInstanceCounter = 0;

const PhotoUpload = ({
  label, hint, isRequired, max, files, onChange, isHighlighted,
  allowComment, commentValue, onCommentChange, existingFiles, onRemoveExisting,
}) => {
  const [isCompressing, setIsCompressing] = useState(false);

  // The underlying Upload component builds its input id from label + uniqueId.
  // Every PhotoUpload on the page must get its own uniqueId, otherwise several
  // fields end up with the same id and clicking "browse" opens/fills the
  // wrong field (whichever one happens to be first in the DOM).
  const instanceIdRef = useRef();
  if (!instanceIdRef.current) {
    uploadInstanceCounter += 1;
    instanceIdRef.current = `photo-upload-${uploadInstanceCounter}`;
  }

  const previewUrls = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);

  useEffect(() => () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    // eslint-disable-next-line
  }, [previewUrls]);

  const remainingSlots = max - files.length - existingFiles.length;

  const onFileInput = async (selectedFiles) => {
    setIsCompressing(true);
    const compressedFiles = await compressImages(selectedFiles);
    setIsCompressing(false);

    const merged = [...files, ...compressedFiles].slice(0, max);
    onChange(merged);
  };

  const onRemove = (index) => {
    onChange(files.filter((_file, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700">
        {label}
        {isRequired && (
          <sup className="text-red-regular">*</sup>
        )}
        {max > 1 && (
          <span className="text-xs font-normal text-gray-500 ml-1">{`(up to ${max})`}</span>
        )}
      </label>
      {hint && (
        <p className="text-xs text-gray-500 mb-1">{hint}</p>
      )}

      {(existingFiles.length > 0 || files.length > 0) && (
        <div className="flex flex-wrap gap-3 mt-2 mb-2">
          {existingFiles.map((file) => (
            <div
              key={file.id}
              className="relative w-24 h-24 rounded-md border border-gray-300 overflow-hidden"
            >
              <img src={file.thumbnail_url || file.url} alt={file.name || 'existing upload'} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemoveExisting(file.id)}
                className="absolute top-0.5 right-0.5 bg-white rounded-full shadow p-0.5 hover:bg-gray-100"
                aria-label="Replace this file"
                title="Replace this file"
              >
                <XIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          ))}
          {files.map((file, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${file.name}-${index}`}
              className="relative w-24 h-24 rounded-md border border-gray-300 overflow-hidden"
            >
              {file.type?.startsWith('image/') ? (
                <img src={previewUrls[index]} alt={file.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-center p-1 break-all">
                  {file.name}
                </div>
              )}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute top-0.5 right-0.5 bg-white rounded-full shadow p-0.5 hover:bg-gray-100"
                aria-label={`Remove ${file.name}`}
              >
                <XIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          ))}
        </div>
      )}

      {isCompressing && (
        <p className="text-xs text-gray-500 mb-1">Optimizing image…</p>
      )}

      {!isCompressing && remainingSlots > 0 && (
        <Upload
          extension="image"
          isMultiple={remainingSlots > 1}
          uniqueId={instanceIdRef.current}
          isHighlighted={isHighlighted}
          handleFileInput={onFileInput}
        />
      )}

      {allowComment && (
        <input
          type="text"
          value={commentValue || ''}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder="Comment on failure (if no photo)"
          className="mt-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500
            block w-full sm:text-sm border-gray-300 rounded-md"
        />
      )}
    </div>
  );
};

PhotoUpload.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  isRequired: PropTypes.bool,
  max: PropTypes.number,
  files: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  isHighlighted: PropTypes.bool,
  allowComment: PropTypes.bool,
  commentValue: PropTypes.string,
  onCommentChange: PropTypes.func,
  existingFiles: PropTypes.array,
  onRemoveExisting: PropTypes.func,
};

PhotoUpload.defaultProps = {
  hint: '',
  isRequired: false,
  max: 1,
  files: [],
  isHighlighted: false,
  allowComment: false,
  commentValue: '',
  onCommentChange: () => {},
  existingFiles: [],
  onRemoveExisting: () => {},
};

export default PhotoUpload;
